import { DEFAULT_PLATFORM_BRANDING, type PlatformBranding } from "@dentos/contracts";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./api";

/*
 * The product's name and logo (Platform admin → Branding). Every screen reads it from here, never from a
 * hard-coded string. The last value is remembered on this device so a reload shows the right name at once;
 * the server copy replaces it a moment later.
 */

const STORAGE_KEY = "platform-branding";

function remembered(): PlatformBranding {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_PLATFORM_BRANDING, ...(JSON.parse(raw) as Partial<PlatformBranding>) };
  } catch {
    // storage blocked (private window): use the default
  }
  return DEFAULT_PLATFORM_BRANDING;
}

interface BrandingState {
  branding: PlatformBranding;
  /** Re-reads the branding (after Platform admin saves a change). */
  refresh: () => Promise<void>;
}

const Ctx = createContext<BrandingState>({ branding: DEFAULT_PLATFORM_BRANDING, refresh: async () => undefined });

/** Points the browser tab icon at the product icon (or the built-in one). */
function applyIcon(b: PlatformBranding) {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) return;
  link.href = b.iconUrl ?? b.logoUrl ?? "/icon.svg";
  link.type = (b.iconUrl ?? b.logoUrl) ? "" : "image/svg+xml";
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<PlatformBranding>(remembered);
  const refresh = useCallback(async () => {
    try {
      const b = await api.get<PlatformBranding>("/api/public/branding");
      setBranding(b);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
      } catch {
        // not remembered on this device; fine
      }
    } catch {
      // keep what we have; the next page load tries again
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    applyIcon(branding);
    if (/^(Loading)?$/.test(document.title)) document.title = branding.productName;
  }, [branding]);
  return <Ctx.Provider value={{ branding, refresh }}>{children}</Ctx.Provider>;
}

/** The product name, logo and support contacts. */
export const useBranding = () => useContext(Ctx).branding;
export const useRefreshBranding = () => useContext(Ctx).refresh;

/** Built-in mark when no logo has been uploaded. */
export function BrandMark({ className = "size-8" }: { className?: string }) {
  const b = useBranding();
  return <img src={b.logoUrl ?? "/icon.svg"} alt="" className={`${className} object-contain`} />;
}
